import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import usePageTitle from '../hooks/usePageTitle';
import registerSuccessState, {
  CONFIRM_TIMEOUT_MS,
} from '../registration/registerSuccessState';

const TITLE_BY_STATE = {
  loading: 'Registration',
  confirming: 'Confirming Registration',
  complete: 'Registration Complete',
  'not-found': 'Registration Not Found',
};

function RegisterSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { isAuthenticated, user, loading: authLoading } = useApp();

  // The Stripe webhook writes the organization onto the account, and the app
  // context watches that document, so the wait normally ends by itself. This
  // is the ceiling on it.
  const [waitedOut, setWaitedOut] = useState(false);

  const state = registerSuccessState({
    authLoading,
    isAuthenticated,
    user,
    sessionId,
    waitedOut,
  });

  usePageTitle(TITLE_BY_STATE[state]);

  useEffect(() => {
    if (state !== 'confirming') {
      return undefined;
    }
    const timer = setTimeout(() => setWaitedOut(true), CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F8F9FB' }}>
      {/* Header */}
      <nav className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center p-4">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/logo.png"
                alt="One Stop Greek Logo"
                className="h-10 w-10"
              />
              <span className="text-2xl font-bold" style={{ color: '#a60533' }}>
                One Stop Greek
              </span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center">
          {state === 'loading' && <LoadingCard />}
          {state === 'confirming' && <ConfirmingCard />}
          {state === 'complete' && <CompleteCard sessionId={sessionId} />}
          {state === 'not-found' && <NotFoundCard />}
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 text-center">
        <p className="text-sm text-gray-500">
          © {new Date().getFullYear()} One Stop Greek. All rights reserved.
        </p>
      </div>
    </div>
  );
}

function LoadingCard() {
  return (
    <div className="flex justify-center py-8">
      <div
        className="animate-spin rounded-full h-12 w-12 border-b-2"
        style={{ borderColor: '#a60533' }}
      ></div>
    </div>
  );
}

function ConfirmingCard() {
  return (
    <div role="status">
      <div className="flex justify-center mb-6">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: '#a60533' }}
        ></div>
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-3">
        Confirming your registration
      </h1>
      <p className="text-gray-600">
        This takes a few seconds after a payment. Keep this page open.
      </p>
    </div>
  );
}

function CompleteCard({ sessionId }) {
  return (
    <>
      {/* Success Icon */}
      <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e8f5e9' }}>
        <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-4">
        Registration Complete!
      </h1>

      <p className="text-gray-600 mb-6">
        Your chapter has been successfully registered. We've sent a confirmation email with your login details and organization code.
      </p>

      {/* Important Info Box */}
      <div className="rounded-lg p-4 mb-6 text-left" style={{ backgroundColor: '#fce7ed' }}>
        <h3 className="font-semibold mb-2" style={{ color: '#a60533' }}>What's Next?</h3>
        <ul className="space-y-2 text-sm" style={{ color: '#8a0429' }}>
          <li className="flex items-start">
            <span className="mr-2">1.</span>
            Download the One Stop Greek app from the App Store
          </li>
          <li className="flex items-start">
            <span className="mr-2">2.</span>
            Log in with the email and password you just created
          </li>
          <li className="flex items-start">
            <span className="mr-2">3.</span>
            Share your organization code with chapter members
          </li>
          <li className="flex items-start">
            <span className="mr-2">4.</span>
            Start tracking study points!
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <a
          href="https://apps.apple.com/app/one-stop-greek"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full py-3 px-6 text-white rounded-lg font-semibold transition-all duration-200"
          style={{ backgroundColor: '#a60533' }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#8a0429'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#a60533'}
        >
          Download the App
        </a>

        <Link
          to="/login"
          className="block w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 border-2"
          style={{ color: '#a60533', borderColor: '#a60533' }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#fce7ed'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          Go to Web Login
        </Link>
      </div>

      <SupportLine />

      {sessionId && (
        <p className="mt-4 text-xs text-gray-400">
          Reference: {sessionId.substring(0, 20)}...
        </p>
      )}
    </>
  );
}

function NotFoundCard() {
  return (
    <>
      <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6 bg-gray-100">
        <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-4">
        No registration found
      </h1>

      <p className="text-gray-600 mb-6">
        This link does not carry a completed registration. Sign in and register
        your chapter to start, or write to us if you have already paid.
      </p>

      <div className="space-y-3">
        <Link
          to="/register"
          className="block w-full py-3 px-6 text-white rounded-lg font-semibold transition-all duration-200"
          style={{ backgroundColor: '#a60533' }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#8a0429'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#a60533'}
        >
          Register your chapter
        </Link>

        <Link
          to="/login"
          className="block w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 border-2"
          style={{ color: '#a60533', borderColor: '#a60533' }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#fce7ed'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          Go to Web Login
        </Link>
      </div>

      <SupportLine />
    </>
  );
}

function SupportLine() {
  return (
    <p className="mt-6 text-sm text-gray-500">
      Questions? Contact us at{' '}
      <a
        href="mailto:support@onestopgreek.com"
        style={{ color: '#a60533' }}
        className="hover:underline"
      >
        support@onestopgreek.com
      </a>
    </p>
  );
}

export default RegisterSuccessPage;
